import type { RequestFormValues } from '@pelicargo/types';
import type { AccountType, FreightService } from '@prisma/client';

// These names are mostly arbitrary, I'm just coming up with them on the fly based on existing use
// Jer is going to kill me. Both Tran and Becker.
export const emailColorMap = {
  black: ['#000000'],
  richBlack: ['#231F20'],
  darkerGray: ['#2e2e32', '#2E2E32'],
  darkGray: ['#333333', '#33373d'],
  infoBackground: ['#518990'],
  gray: ['#555555', '#52555a'],
  lightGray: ['#636363'],
  link: ['#81909D'],
  flightTracking: ['#bebec2'],
  rowBackground: ['#dfecf2'],
  lightBorder: ['#e5dfdf', '#dadada'],
  primary: ['#ed7500'],
  offWhite: ['#f1f6fd'],
  white: ['#ffffff'],
} as const satisfies Record<string, string[]>;

export const baseEmailTheme = Object.keys(emailColorMap).reduce(
  (agg, key) => {
    agg[key] = emailColorMap[key][0];
    return agg;
  },
  {} as { -readonly [id in keyof typeof emailColorMap]: (typeof emailColorMap)[id][0] },
);

type RequiredTenantAssets =
  | 'logo'
  | 'logo-dark'
  | 'logo-dark-tagline'
  | 'logo-light'
  | 'logo-light-tagline'
  | 'twitterUrl'
  | 'facebookUrl'
  | 'linkedinUrl'
  | 'siteUrl';
export const baseEmailAssets = {
  facebook: 'https://raw.githubusercontent.com/pelicargo/assets/master/facebook.png',
  flight: 'https://raw.githubusercontent.com/pelicargo/assets/master/flight.png',
  landing: 'https://raw.githubusercontent.com/pelicargo/assets/master/landing.png',
  linkedin: 'https://raw.githubusercontent.com/pelicargo/assets/master/linkedin.png',
  pivot: 'https://raw.githubusercontent.com/pelicargo/assets/master/pivot.png',
  takeoff: 'https://raw.githubusercontent.com/pelicargo/assets/master/takeoff.png',
  twitter: 'https://raw.githubusercontent.com/pelicargo/assets/master/twitter.png',
  'welcome-quote': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-quote.jpg',
  'welcome-save': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-save.jpg',
  'welcome-ship': 'https://raw.githubusercontent.com/pelicargo/assets/master/welcome-ship.jpg',
} as const;

export type RequestDrawers =
  | 'ProviderQuoting'
  | 'RequestClarifications'
  | 'QuoteUpdate'
  | 'AirlineBooking'
  | 'BookingConfirmation'
  | 'BookingChange'
  | 'BookingCancel'
  | 'NewRate'
  | 'Contact'
  | 'RequestSchedule'
  | 'GenericEmailToFF'
  | 'StatusUpdate';

export type TenantInfo<ID extends string> = {
  id: ID;
  name: string;
  // Domains served by this tenant. Every entry matches the exact host or any subdomain of it,
  // so don't anchor regex entries — write just the domain pattern (e.g. /keet\.staging-\d+\.pelicargo\.dev/).
  domains: (string | RegExp)[];
  // For us - no restrictions
  isSystemTenant: boolean;
  customerTheme: {
    colors: Record<string, Record<string, string>>;
    logoDims: { width: number; height: number };
    fullLogoDims: {
      height: number | string;
      width: number | string;
    };
    // The rest of the themeable surface, keyed exactly like `CustomerThemeSchema`
    // (`packages/utils/src/models/customer-theme/schema.ts`): `radii`, `semantic`, `fonts`,
    // `heading`, `button`, `field`, `input`, `label`.
    //
    // Values set here are baked in as the `var()` fallbacks when the Chakra theme is built, so
    // they are correct on the very first paint and cost nothing at runtime. The DB theme edited in
    // admin overrides them per tenant without a deploy. This is the right home for a tenant's
    // permanent look; the DB layer is for tweaks.
    //
    // Deliberately not typed as `CustomerTheme`: this package is below `@pelicargo/utils` in the
    // dependency graph. `resolveStaticThemeValue` in the customer app narrows it.
    tokens?: Record<string, unknown>;
  };
  emailTheme: Record<keyof typeof emailColorMap, string>;
  emailAssets: Record<keyof typeof baseEmailAssets | RequiredTenantAssets, string>;
  accountTypes: {
    [key in AccountType]: boolean;
  };
  admin: {
    cruds: {
      accounts: boolean;
      providers: boolean;
      airports: boolean;
      contacts: boolean;
      instructions: boolean;
      users: boolean;
      backlogs: boolean;
      'ai-inbox': boolean;
      surcharges: boolean;
      rates: boolean;
      'route-editor': boolean;
      configuration: boolean;
    };
    inbox: {
      requestDrawers: {
        [key in RequestDrawers]: boolean;
      };
    };
    features: {
      instructionCreation: boolean;
    };
  };
  customer?: {
    // Freight services offered for customer quote creation, keyed by account type, with a `default`
    // for account types not listed. Falls back to the built-in per-account-type defaults in
    // getFreightServices (IATA/IAC -> [AFR], UNCERTIFIED -> [EXW, FCA], default -> all) when omitted.
    freightServices?: Partial<Record<AccountType, FreightService[]>> & { default: FreightService[] };
    // Initial values merged over the built-in request-form defaults (e.g. pre-checked AES / HAWB).
    requestFormDefaults: Pick<RequestFormValues, 'freight_service' | 'is_consolidated' | 'extra_freight_services'>;
    // Per-tenant copy overrides, keyed by the slots in
    // `packages/utils/src/models/customer-theme/copy.ts`. This is the layer that is correct on the
    // very first render, so a tenant's permanent wording belongs here rather than in the DB theme
    // (which only arrives after the config query settles). Omitted keys fall back to the built-in
    // defaults. Typed loosely as a string record to avoid a `utils -> tenants` dependency cycle;
    // `resolveCustomerCopy` narrows it.
    copy?: Record<string, string>;

    // The login/signup value-prop panel. `showVideo` is opt-in because the `authPanelVideo` and
    // `authPanelPoster` assets are symlinked to Pelicargo's own product demo in every tenant's
    // folder, so defaulting it on would play Pelicargo marketing on a white-label login page.
    // A tenant turns it on once it has committed its own demo over those slots.
    authPanel?: {
      showVideo?: boolean;
    };

    // Whether the Front live-chat support entry points are shown. Defaults to false (hidden) when omitted.
    supportChat?: boolean;
    // Reamaze chat opened from the Support/Chat buttons instead of Front. Values are data only; the
    // loader script is hardcoded in the customer bundle (`customer/src/utils/reamaze.ts`).
    reamaze?: {
      account: string;
      mailbox: string;
      slug: string;
      widgetColor: string;
      greeting: string;
      confirmationMessage: string;
    };
    // Freight networks a company can select. Omit = hidden. `showOnSignup` defaults false (edit + admin only).
    networks?: {
      showOnSignup?: boolean;
      options: readonly string[];
    };
  };
  termsOfService: string;
  quoteTerms?: readonly string[];
  helpUrl?: string;
};
